import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Globe,
  Pencil,
  RotateCcw,
  Trash2,
} from 'lucide-react'

import { Badge } from '@/ui/components/common/Badge'
import { FactScoreInline } from '@/ui/components/common/FactScoreInline'
import { MarkdownView } from '@/ui/components/common/MarkdownView'
import { classNames } from '@/ui/utils/main'
import { markdownContainsImage } from '@/ui/utils/markdown'

import type { Fact } from '@/domains/types'

const DESCRIPTION_THRESHOLD = 200

interface FactTileProps {
  fact: Fact
  leadId: string
  canEdit: boolean
  onEdit: () => void
  onDelete: () => void
  onPublish?: () => void
  onUnpublish?: () => void
  onResetVotes?: () => void
  /** Fully-wired drag handle element (icon + dnd-kit listeners), if the list is sortable. */
  dragHandle?: ReactNode
}

/**
 * Single fact tile in the lead-detail facts gallery: thumbnail + title +
 * description + url, the whole tile linking to the external source, with a
 * hover-revealed delete button in the top-right.
 */
export function FactTile({
  fact,
  leadId,
  canEdit,
  onEdit,
  onDelete,
  onPublish,
  onUnpublish,
  onResetVotes,
  dragHandle,
}: FactTileProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden flex items-stretch transition-all duration-200 hover:border-primary/50 hover:shadow-md group">
      {dragHandle && (
        <div className="flex items-center justify-center px-1 shrink-0 border-r border-border/50 bg-muted/40">
          {dragHandle}
        </div>
      )}

      {/* Left: image, only when one exists */}
      {fact.imageUrl && (
        <div className="w-24 sm:w-32 shrink-0 overflow-hidden bg-muted flex items-center justify-center">
          <img
            src={fact.imageUrl}
            alt={fact.title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </div>
      )}

      {/* Center: content */}
      <div className="flex-1 px-4 py-6 border-l-2 border-l-primary/60 min-w-0 min-h-[88px] flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-sm text-foreground line-clamp-1">{fact.title}</h4>
            {fact.published ? (
              <Badge
                variant="secondary"
                className="text-xs shrink-0 text-success border-success/30 bg-success/10"
              >
                {t('lead_detail.fact_published')}
              </Badge>
            ) : (
              <Badge
                variant="secondary"
                className="text-xs shrink-0 text-warning border-warning/30 bg-warning/10"
              >
                {t('lead_detail.fact_unpublished')}
              </Badge>
            )}
          </div>
          {fact.description && (
            <div>
              <MarkdownView
                content={fact.description}
                compact={!expanded}
                className={classNames(
                  'text-xs text-muted-foreground',
                  !expanded && 'max-h-24 overflow-hidden',
                )}
              />
              {(fact.description.length > DESCRIPTION_THRESHOLD ||
                markdownContainsImage(fact.description)) && (
                <div className="flex justify-end mt-0.5">
                  <button
                    type="button"
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      setExpanded(!expanded)
                    }}
                    className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {expanded ? t('lead_detail.fact_see_less') : t('lead_detail.fact_see_more')}
                    {expanded ? (
                      <ChevronUp className="w-3.5 h-3.5" />
                    ) : (
                      <ChevronDown className="w-3.5 h-3.5" />
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col items-start mt-3 mb-3 gap-1.5">
          {fact.url && (
            <a
              href={fact.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
              className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded border border-border bg-muted/30 hover:bg-muted/50 text-foreground transition-colors no-underline shrink-0"
            >
              <ExternalLink className="w-3 h-3" />
              {t('lead_detail.fact_source_button')}
            </a>
          )}
          {(fact.submittedByTwitchUsername ?? fact.submittedByUsername) && (
            <p className="text-xs text-muted-foreground">
              {t('common.submitted_by')}:{' '}
              <span className="text-foreground/80">
                {fact.submittedByTwitchUsername ?? fact.submittedByUsername}
              </span>
            </p>
          )}
        </div>
      </div>

      {/* Right: score + actions — vertically centered, not clickable as link */}
      <div className="flex items-center gap-3 px-3 py-4 shrink-0 border-l border-border/50">
        <FactScoreInline leadId={leadId} factId={fact.id} showBoth />

        <div className="flex flex-col gap-1">
          {!fact.published && onPublish && (
            <button
              onClick={onPublish}
              title={t('lead_detail.fact_publish_button')}
              className="p-1.5 rounded-lg bg-background/90 border border-border text-success hover:bg-success/10 transition-all"
            >
              <Globe className="w-3.5 h-3.5" />
            </button>
          )}
          {fact.published && onUnpublish && (
            <button
              onClick={onUnpublish}
              title={t('lead_detail.fact_unpublish_button')}
              className="p-1.5 rounded-lg bg-background/90 border border-border text-warning hover:bg-warning/10 transition-all"
            >
              <Globe className="w-3.5 h-3.5" />
            </button>
          )}
          {canEdit && (
            <button
              onClick={onEdit}
              className="p-1.5 rounded-lg bg-background/90 border border-border text-muted-foreground hover:bg-muted/50 transition-all"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
          )}
          {onResetVotes && (
            <button
              onClick={onResetVotes}
              title={t('lead_detail.fact_reset_votes')}
              className="p-1.5 rounded-lg bg-background/90 border border-border text-warning hover:bg-warning/10 transition-all"
            >
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={onDelete}
            className="p-1.5 rounded-lg bg-background/90 border border-border text-destructive hover:bg-destructive/10 transition-all"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  )
}
